import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { format, isPast, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertCircle, Calendar, CheckCircle2, Circle, Clock, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Header } from '../components/Layout/Header';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { personalTasksApi } from '../services/api';
import { PersonalTask, PersonalTaskRecurrence } from '../types';

type TaskFilter = 'all' | 'overdue' | 'today' | 'completed';

const recurrenceLabels: Record<PersonalTaskRecurrence, string> = {
  none: 'Não repete',
  daily: 'Diariamente',
  weekly: 'Semanalmente',
  monthly: 'Mensalmente',
};

interface TaskFormData {
  title: string;
  description: string;
  due_date: string;
  recurrence: PersonalTaskRecurrence;
}

function emptyForm(): TaskFormData {
  return { title: '', description: '', due_date: '', recurrence: 'none' };
}

function taskToForm(task: PersonalTask): TaskFormData {
  return {
    title: task.title,
    description: task.description || '',
    due_date: task.due_date ? task.due_date.slice(0, 10) : '',
    recurrence: task.recurrence,
  };
}

// due_date is a date-only column (no time); parsing it with `new Date(string)` reads it as
// UTC midnight, which shifts a day back once rendered in any timezone behind UTC. Building
// the Date from its y/m/d parts keeps it anchored to local midnight instead.
function parseDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function MyTasksPage() {
  const [tasks, setTasks] = useState<PersonalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TaskFilter>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [form, setForm] = useState<TaskFormData>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadTasks = useCallback(() => {
    setLoading(true);
    personalTasksApi.getAll()
      .then(res => setTasks(res.data))
      .catch(() => toast.error('Falha ao carregar tarefas'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const openCreateModal = () => {
    setEditingTaskId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEditModal = (task: PersonalTask) => {
    setEditingTaskId(task.id);
    setForm(taskToForm(task));
    setIsModalOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error('Informe o título da tarefa');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        due_date: form.due_date || undefined,
        recurrence: form.recurrence,
      };
      if (editingTaskId) {
        await personalTasksApi.update(editingTaskId, payload);
        toast.success('Tarefa atualizada!');
      } else {
        await personalTasksApi.create(payload);
        toast.success('Tarefa criada!');
      }
      setIsModalOpen(false);
      loadTasks();
    } catch {
      toast.error(editingTaskId ? 'Falha ao atualizar tarefa' : 'Falha ao criar tarefa');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (task: PersonalTask) => {
    setTogglingId(task.id);
    try {
      if (task.is_done) {
        await personalTasksApi.reopen(task.id);
        toast.success('Tarefa reaberta');
      } else {
        const res = await personalTasksApi.complete(task.id);
        const spawnedNext = res.data?.nextTask;
        toast.success(spawnedNext ? 'Tarefa concluída! Próxima ocorrência criada.' : 'Tarefa concluída!');
      }
      loadTasks();
    } catch {
      toast.error('Falha ao atualizar tarefa');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (task: PersonalTask) => {
    const confirmed = window.confirm(`Excluir a tarefa "${task.title}"? Esta ação não pode ser desfeita.`);
    if (!confirmed) return;
    setDeletingId(task.id);
    try {
      await personalTasksApi.delete(task.id);
      toast.success('Tarefa excluída');
      loadTasks();
    } catch {
      toast.error('Falha ao excluir tarefa');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'overdue') return !!task.due_date && isPast(parseDateOnly(task.due_date)) && !task.is_done;
    if (filter === 'today') return !!task.due_date && isToday(parseDateOnly(task.due_date));
    if (filter === 'completed') return task.is_done;
    return true;
  });

  const filters: Array<{ key: TaskFilter; label: string; icon: React.ReactNode }> = [
    { key: 'all', label: 'Todas as Tarefas', icon: <Clock size={15} /> },
    { key: 'today', label: 'Vence Hoje', icon: <Calendar size={15} /> },
    { key: 'overdue', label: 'Atrasadas', icon: <AlertCircle size={15} /> },
    { key: 'completed', label: 'Concluídas', icon: <CheckCircle2 size={15} /> },
  ];

  return (
    <div>
      <Header title="Minhas Tarefas" />
      <div className="p-6">
        <div className="flex items-center justify-start mb-6 flex-wrap gap-3">
          <div className="flex flex-wrap justify-start gap-2">
            {filters.map(item => (
              <button
                key={item.key}
                onClick={() => setFilter(item.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === item.key ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus size={15} />
            Nova Tarefa
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={24} className="text-primary-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Nenhuma tarefa encontrada</h3>
            <p className="text-gray-500 mt-1">Tudo em dia por aqui</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map(task => {
              const isOverdue = !!task.due_date && isPast(parseDateOnly(task.due_date)) && !task.is_done;
              return (
                <div
                  key={task.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:shadow-sm transition"
                >
                  <button
                    type="button"
                    onClick={() => handleToggle(task)}
                    disabled={togglingId === task.id}
                    className={`shrink-0 rounded-full p-1 transition-colors disabled:opacity-50 ${task.is_done ? 'text-emerald-600' : 'text-gray-300 hover:text-primary-500'}`}
                    title={task.is_done ? 'Reabrir tarefa' : 'Concluir tarefa'}
                  >
                    {task.is_done ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${task.is_done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</p>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{task.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {task.recurrence !== 'none' && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <RotateCcw size={12} />
                        {recurrenceLabels[task.recurrence]}
                      </span>
                    )}
                    {task.due_date && (
                      <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>
                        {isOverdue ? <AlertCircle size={12} /> : <Calendar size={12} />}
                        {isOverdue ? 'Atrasada • ' : ''}
                        {format(parseDateOnly(task.due_date), "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => openEditModal(task)}
                      className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                      title="Editar tarefa"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(task)}
                      disabled={deletingId === task.id}
                      className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                      title="Excluir tarefa"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingTaskId ? 'Editar tarefa' : 'Nova tarefa'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              required
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex.: Ir ao mercado"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm min-h-[72px] resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repetir</label>
              <select
                value={form.recurrence}
                onChange={e => setForm(prev => ({ ...prev, recurrence: e.target.value as PersonalTaskRecurrence }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="none">Não repete</option>
                <option value="daily">Diariamente</option>
                <option value="weekly">Semanalmente</option>
                <option value="monthly">Mensalmente</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1 justify-center">Cancelar</Button>
            <Button type="submit" loading={saving} className="flex-1 justify-center">{editingTaskId ? 'Salvar alterações' : 'Salvar tarefa'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
